// Shared publisher form used by both /self-service/onboard (create) and
// /self-service/admin/publishers/:slug (edit). Owns its own state; the parent
// page hands in initial values and a submit handler that hits the right edge
// function.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2, Palette, DollarSign, MapPin, Tv, CheckCircle2, XCircle,
  Sparkles, Loader2, Plus, Trash2, AlertTriangle,
} from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import { DMA_REGIONS } from '../../../data/dmaList';
import type { PublisherConfig } from '../../../types/selfService';
import { useAuth } from '../auth/useAuth';
import LogoUploader from './LogoUploader';

// --- Sleek style tokens ----------------------------------------------------
// Mirrors the look of the wizard's _shared.tsx: subtle borders, rounded-xl,
// uppercase tracked labels, soft slate background.

const inputClass =
  'block w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-300 ' +
  'focus:outline-none focus:border-slate-900 focus:ring-0 transition-colors';
const readonlyInputClass = inputClass + ' bg-slate-50 text-slate-400 cursor-not-allowed';
const labelClass = 'block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5';

import { inferStateFromDmaName } from './inferStateFromDmaName';
import StateSelector from '../shared/StateSelector';

// `channels` is a raw string while editing so the user can freely type
// commas + spaces — we split at save time. `coveredStates` is driven by
// the StateSelector multi-select so it stays as a list of 2-letter codes.
interface MarketRow { dmaCode: string; dmaName: string; channels: string; coveredStates: string[] }

// CTV-431: header-render-scale options surfaced in the admin UI. Backend
// accepts any number in [0.5, 3.0] but we keep the picker to a handful of
// presets so admins don't tune-by-pixel.
const LOGO_SCALE_OPTIONS: { value: number; label: string }[] = [
  { value: 1.0, label: '1×' },
  { value: 1.25, label: '1.25×' },
  { value: 1.5, label: '1.5×' },
  { value: 2.0, label: '2×' },
];

export interface PublisherFormPayload {
  slug: string;
  name: string;
  displayName: string | null;
  // Required when a super_admin creates a channel (their JWT carries no
  // organization_id). Org_admins can omit it — the API derives org from
  // their JWT and ignores any body override.
  organizationId?: string | null;
  logoUrl: string;
  logoInvert: boolean;
  logoScale: number;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string | null;
  fontUrl: string | null;
  coverage: 'regional' | 'national';
  markets: { dmaCode: string; dmaName: string; channels: string[]; coveredStates: string[] }[];
  // CTV-431: one Run-of-Network product per station, server-managed. The form
  // only sends the admin-controlled CPM and the included Publica sub-channel
  // names; name + description are hardcoded in the backend.
  ronCpm: number;
  ronIncludedSubChannels: string[];
  revSharePercent: number;
  minBudget: number;
  minCampaignDays: number;
  supportEmail: string;
  termsUrl: string;
}

interface Props {
  mode: 'create' | 'edit';
  initial: PublisherConfig | null;
  onSubmit: (payload: PublisherFormPayload) => Promise<{ slug: string }>;
  submitLabel?: string;
}

const PublisherForm: React.FC<Props> = ({ mode, initial, onSubmit, submitLabel }) => {
  const { user } = useAuth();
  const isSuperAdminCreating = mode === 'create' && user?.rawRole === 'super_admin';

  // Super_admins onboard channels across organizations and must pick the
  // owning org (their JWT carries no organization_id). Org_admins onboard
  // implicitly into their own org — no picker needed.
  const [organizationId, setOrganizationId] = useState<string>(initial?.organizationId ?? '');
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);

  useEffect(() => {
    if (!isSuperAdminCreating) return;
    let cancelled = false;
    setOrganizationsLoading(true);
    (async () => {
      try {
        const rows = await apiClient.get<{ id: string; name: string }[]>('/admin/organizations');
        if (!cancelled) setOrganizations(rows ?? []);
      } catch (e) {
        console.warn('[PublisherForm] failed to load organizations:', e);
      } finally {
        if (!cancelled) setOrganizationsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdminCreating]);

  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [website, setWebsite] = useState('');

  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? '');
  const [logoNotTransparent, setLogoNotTransparent] = useState(false);
  const [logoInvert, setLogoInvert] = useState(initial?.logoInvert ?? false);
  const [logoScale, setLogoScale] = useState<number>(initial?.logoScale ?? 1);
  const [primaryColor, setPrimaryColor] = useState(initial?.primaryColor ?? '');
  const [secondaryColor, setSecondaryColor] = useState(initial?.secondaryColor ?? '');
  const [accentColor, setAccentColor] = useState(initial?.accentColor ?? '');
  const [fontFamily, setFontFamily] = useState(initial?.fontFamily ?? '');
  const [fontUrl, setFontUrl] = useState(initial?.fontUrl ?? '');

  const [coverage, setCoverage] = useState<'regional' | 'national'>(initial?.coverage ?? 'regional');
  const [supportEmail, setSupportEmail] = useState(initial?.supportEmail ?? '');
  const [termsUrl, setTermsUrl] = useState(initial?.termsUrl ?? '#');
  // Two-decimal precision: negotiated rates like 15.5% must round-trip
  // load → edit → submit without truncation (× 10000 / 100, not × 1000 / 10).
  const [revSharePercent, setRevSharePercent] = useState(
    // New publishers default to the 20% product rev share (matches the
    // API/DB defaults).
    initial ? String(Math.round(initial.revSharePercent * 10000) / 100) : '20',
  );
  const [minBudget, setMinBudget] = useState(initial ? String(initial.minBudget) : '250');
  const [minDays, setMinDays] = useState(initial ? String(initial.minCampaignDays) : '7');

  const [markets, setMarkets] = useState<MarketRow[]>(
    initial?.markets.map((m) => ({
      dmaCode: m.dmaCode,
      dmaName: m.dmaName,
      channels: m.channels.join(', '),
      coveredStates: m.coveredStates ?? [],
    })) ?? [],
  );

  // --- CTV-431: Run-of-Network product state -----------------------------
  // CPM is the only commercial knob; sub-channels are the canonical Publica
  // list (refetched per save), with an admin checkbox-driven inclusion set.
  const initialRon = initial?.products?.[0];
  const [ronCpm, setRonCpm] = useState<string>(
    initialRon && initialRon.cpm != null ? String(initialRon.cpm) : '',
  );
  const [availableSubChannels, setAvailableSubChannels] = useState<string[]>(
    initialRon?.subChannels ?? [],
  );
  const [includedSubChannels, setIncludedSubChannels] = useState<Set<string>>(
    () => new Set(initialRon?.subChannels ?? []),
  );
  const [subChannelsLoading, setSubChannelsLoading] = useState(false);
  const [subChannelsWarning, setSubChannelsWarning] = useState<string | null>(null);

  const [extracting, setExtracting] = useState(false);
  const [extractingLogo, setExtractingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Refetch the canonical Publica sub-channel list whenever the org or
  // internal name changes. Mirrors what the backend will do on save, so
  // admins see the same set of checkboxes the server will validate against.
  // Edit mode: organizationId is preloaded from the PublisherConfig, so this
  // also fires on initial mount with the existing station's name. Create
  // mode (super_admin): fires once both fields are populated.
  useEffect(() => {
    const trimmedName = name.trim();
    if (!organizationId || !trimmedName) {
      // Don't wipe the checkbox list while the admin is mid-typing — only
      // clear it when both inputs are missing (which is the initial-create
      // state). On edit, organizationId is always set, so this branch never
      // resets the loaded list.
      if (!organizationId && !trimmedName) {
        setAvailableSubChannels([]);
        setIncludedSubChannels(new Set());
        setSubChannelsWarning(null);
      }
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSubChannelsLoading(true);
      try {
        const result = await apiClient.get<{ channels: string[] }>(
          `/admin/organizations/${organizationId}/publica-channels?stationName=${encodeURIComponent(trimmedName)}`,
        );
        if (cancelled) return;
        const channels = result?.channels ?? [];
        setAvailableSubChannels(channels);
        if (channels.length === 0) {
          setIncludedSubChannels(new Set());
          setSubChannelsWarning(
            `No Publica channels matched "${trimmedName}". Save will be blocked until inventory exists.`,
          );
        } else {
          setSubChannelsWarning(null);
          // Newly-detected sub-channels default to included; previously-known
          // ones keep their checkbox state so re-typing the name doesn't undo
          // the admin's deselections.
          setIncludedSubChannels((prev) => {
            const next = new Set<string>();
            for (const c of channels) {
              if (prev.size === 0 || prev.has(c)) next.add(c);
            }
            return next;
          });
        }
      } catch (e) {
        if (cancelled) return;
        setSubChannelsWarning(
          `Could not load Publica channels: ${e instanceof Error ? e.message : 'unknown error'}`,
        );
      } finally {
        if (!cancelled) setSubChannelsLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [organizationId, name]);

  // When initial arrives async (edit mode loads after mount), hydrate state.
  useEffect(() => {
    if (!initial) return;
    setSlug(initial.slug);
    setName(initial.name);
    setDisplayName(initial.displayName ?? '');
    setLogoUrl(initial.logoUrl);
    setLogoInvert(initial.logoInvert ?? false);
    setLogoScale(initial.logoScale ?? 1);
    setPrimaryColor(initial.primaryColor);
    setSecondaryColor(initial.secondaryColor);
    setAccentColor(initial.accentColor);
    setFontFamily(initial.fontFamily);
    setFontUrl(initial.fontUrl ?? '');
    setCoverage(initial.coverage ?? 'regional');
    setSupportEmail(initial.supportEmail ?? '');
    setTermsUrl(initial.termsUrl);
    setRevSharePercent(String(Math.round(initial.revSharePercent * 10000) / 100));
    setMinBudget(String(initial.minBudget));
    setMinDays(String(initial.minCampaignDays));
    setMarkets(initial.markets.map((m) => ({
      dmaCode: m.dmaCode,
      dmaName: m.dmaName,
      channels: m.channels.join(', '),
      coveredStates: m.coveredStates ?? [],
    })));
    const ron = initial.products?.[0];
    if (ron) {
      setRonCpm(String(ron.cpm ?? ''));
      setAvailableSubChannels((prev) => (prev.length > 0 ? prev : ron.subChannels));
      setIncludedSubChannels((prev) => (prev.size > 0 ? prev : new Set(ron.subChannels)));
    }
  }, [initial]);

  const toggleSubChannel = (channel: string) => {
    setIncludedSubChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);
      return next;
    });
  };

  const allChecked = useMemo(
    () =>
      availableSubChannels.length > 0 &&
      availableSubChannels.every((c) => includedSubChannels.has(c)),
    [availableSubChannels, includedSubChannels],
  );

  const handleExtract = async () => {
    if (!website.trim()) { setError('Enter a website URL first'); return; }
    setExtracting(true);
    setError(null);
    try {
      const result = await apiClient.post<{
        logoUrl: string | null;
        logoLikelyTransparent: boolean | null;
        siteTitle: string | null;
        primaryColor: string | null;
        secondaryColor: string | null;
        accentColor: string | null;
        fontFamily: string | null;
        fontUrl: string | null;
        bodyText: string | null;
      }>('/stations/extract-branding', { url: website.trim() });
      if (result.logoUrl) setLogoUrl(result.logoUrl);
      setLogoNotTransparent(result.logoUrl != null && result.logoLikelyTransparent === false);
      if (result.primaryColor) setPrimaryColor(result.primaryColor);
      if (result.secondaryColor) setSecondaryColor(result.secondaryColor);
      if (result.accentColor) setAccentColor(result.accentColor);
      if (result.fontFamily) setFontFamily(result.fontFamily);
      if (result.fontUrl) setFontUrl(result.fontUrl);
      if (result.siteTitle && !displayName) setDisplayName(result.siteTitle);

      let inferredDma: typeof DMA_REGIONS[number] | null = null;
      if (result.bodyText && markets.length === 0) {
        const haystack = ' ' + result.bodyText.toLowerCase() + ' ';
        const scores = DMA_REGIONS.map((d) => {
          const keywords = d.name.split(/[,\-/()]+/).map((k) => k.trim()).filter((k) => k.length >= 4);
          let score = 0;
          for (const kw of keywords) {
            const re = new RegExp(`\\b${kw.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
            const matches = haystack.match(re);
            if (matches) score += matches.length;
          }
          return { dma: d, score };
        }).filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
        if (scores.length > 0 && scores[0].score >= 2) {
          inferredDma = scores[0].dma;
          const autoState = inferStateFromDmaName(inferredDma.name);
          setMarkets([{
            dmaCode: inferredDma.code,
            dmaName: inferredDma.name,
            channels: '',
            coveredStates: autoState ? [autoState] : [],
          }]);
        }
      }

      const anyHit = result.logoUrl || result.primaryColor || result.fontFamily || inferredDma;
      if (!anyHit) setError('Could not auto-detect branding. Fill in fields manually.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  // Re-pull just the logo from the website, leaving colors/font/markets/name
  // untouched — for fixing a bad logo without re-running full branding extract.
  const handleExtractLogo = async () => {
    if (!website.trim()) { setError('Enter a website URL first'); return; }
    setExtractingLogo(true);
    setError(null);
    setLogoNotTransparent(false);
    try {
      const result = await apiClient.post<{
        logoUrl: string | null;
        logoLikelyTransparent: boolean | null;
      }>('/stations/extract-branding', { url: website.trim() });
      if (result.logoUrl) {
        setLogoUrl(result.logoUrl);
        setLogoNotTransparent(result.logoLikelyTransparent === false);
      } else {
        setError('No logo found at that website.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Logo extraction failed');
    } finally {
      setExtractingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setSubmitting(true);
    try {
      if (isSuperAdminCreating && !organizationId) {
        setError('Pick an organization to onboard this channel under.');
        setSubmitting(false);
        return;
      }
      const payload: PublisherFormPayload = {
        slug: slug.trim().toLowerCase(),
        name: name.trim(),
        displayName: displayName.trim() || null,
        organizationId: isSuperAdminCreating ? organizationId : null,
        logoUrl: logoUrl.trim(),
        logoInvert,
        logoScale,
        primaryColor, secondaryColor, accentColor,
        // Empty string is rejected by the API's validator (requireString min 1).
        // Coerce blanks to null so the field is treated as "not set" — branding
        // extraction sometimes misses the font and shouldn't block onboarding.
        fontFamily: fontFamily.trim() || null,
        fontUrl: fontUrl.trim() || null,
        coverage,
        markets: markets
          .filter((m) => m.dmaCode && m.dmaName)
          .map((m) => ({
            dmaCode: m.dmaCode.trim(),
            dmaName: m.dmaName.trim(),
            channels: m.channels.split(',').map((c) => c.trim()).filter(Boolean),
            coveredStates: m.coveredStates,
          })),
        ronCpm: Number(ronCpm) || 0,
        ronIncludedSubChannels: Array.from(includedSubChannels),
        revSharePercent: Number(revSharePercent) / 100,
        minBudget: Number(minBudget),
        minCampaignDays: Number(minDays),
        supportEmail: supportEmail.trim(),
        termsUrl: termsUrl.trim() || '#',
      };
      const { slug: resultSlug } = await onSubmit(payload);
      setSuccessMessage(mode === 'create' ? `Publisher "${resultSlug}" created.` : `Saved.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const updateMarket = (i: number, patch: Partial<MarketRow>) =>
    setMarkets((arr) => arr.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {successMessage && (
        <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
          <p className="text-[13px] text-emerald-800 leading-relaxed">{successMessage}</p>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <XCircle className="w-4 h-4 mt-0.5 text-rose-600 shrink-0" />
          <p className="text-[13px] text-rose-800 leading-relaxed">{error}</p>
        </div>
      )}

      <Section title="Identity" subtitle="The slug becomes the URL: /self-service/{slug}." icon={Building2}>
        {isSuperAdminCreating && (
          <div className="mb-4">
            <label className={labelClass}>Organization</label>
            <select
              required
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              disabled={organizationsLoading}
              className={inputClass}
            >
              <option value="">
                {organizationsLoading ? 'Loading organizations…' : 'Select organization…'}
              </option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-500">
              Which organization owns this channel. Required for super_admins because
              their account isn't tied to a single org.
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Slug (URL key)</label>
            <input
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. wisc"
              className={mode === 'edit' ? readonlyInputClass : inputClass}
              readOnly={mode === 'edit'}
            />
          </div>
          <div>
            <label className={labelClass}>Internal name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="WISC-TV / Channel 3000" className={inputClass} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>
              Consumer-facing display name <span className="font-normal normal-case tracking-normal text-slate-400">(optional — defaults to internal name)</span>
            </label>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Channel 3000" className={inputClass} />
          </div>
          {/* TODO: persist the website URL on `stations` (needs migration:
              add a `website` column, write it on create/update, and roundtrip
              it through PublisherConfig). Pending product discussion. */}
          <div className="sm:col-span-2">
            <label className={labelClass}>
              Website URL <span className="font-normal normal-case tracking-normal text-slate-400">(used to extract branding — not stored)</span>
            </label>
            <div className="flex gap-2">
              <input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://www.channel3000.com"
                className={`${inputClass} flex-1`}
              />
              <button
                type="button"
                onClick={handleExtract}
                disabled={extracting}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-60 transition-colors"
              >
                {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {extracting ? 'Extracting…' : 'Extract branding'}
              </button>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Branding" subtitle="Logo, brand colors, and font that drive the channel's portal theme." icon={Palette}>
        <div className="space-y-5">
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Logo URL</label>
              <div className="flex gap-2">
                <input required value={logoUrl} onChange={(e) => { setLogoUrl(e.target.value); setLogoNotTransparent(false); }} className={`${inputClass} flex-1`} />
                <button
                  type="button"
                  onClick={handleExtractLogo}
                  disabled={extractingLogo}
                  title="Re-pull just the logo from the website URL above"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-2.5 rounded-xl text-[13px] font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
                >
                  {extractingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {extractingLogo ? 'Extracting…' : 'Extract logo'}
                </button>
              </div>
            </div>
            <LogoUploader onUploaded={(url) => { setLogoUrl(url); setLogoNotTransparent(false); }} />
            {logoUrl && (
              // Preview on both light and dark backgrounds: a transparent logo
              // is often white (built for a dark masthead) or dark, so one
              // backdrop alone can hide it entirely. The invert filter mirrors
              // how the portal header renders it, so the toggle previews live.
              <div className="flex gap-2">
                {[
                  { label: 'On light', wrap: 'border-slate-200 bg-slate-50', text: 'text-slate-400' },
                  { label: 'On dark', wrap: 'border-slate-700 bg-slate-900', text: 'text-slate-500' },
                ].map((bg) => (
                  <div key={bg.label} className={`flex-1 flex flex-col items-center gap-1.5 rounded-xl border p-3 ${bg.wrap}`}>
                    <img
                      src={logoUrl}
                      alt="logo preview"
                      className={`max-h-16 object-contain ${logoInvert ? 'brightness-0 invert' : ''}`}
                      style={{ transform: `scale(${logoScale})` }}
                      onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                    />
                    <span className={`text-[10px] font-medium uppercase tracking-[0.08em] ${bg.text}`}>{bg.label}</span>
                  </div>
                ))}
              </div>
            )}
            {logoNotTransparent && (
              <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  This scraped logo has a solid background, so it&rsquo;ll show as a box on the channel&rsquo;s
                  portal. Ask the publisher for a transparent PNG/SVG and upload it above.
                </span>
              </p>
            )}
            <div className="flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={logoInvert}
                  onChange={(e) => setLogoInvert(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                />
                Force logo to white (for dark headers)
              </label>
              <div className="flex items-center gap-2">
                <label className="text-[12px] text-slate-600">Header scale</label>
                <select
                  value={String(logoScale)}
                  onChange={(e) => setLogoScale(Number(e.target.value))}
                  className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[12px] text-slate-900 focus:outline-none focus:border-slate-900"
                >
                  {LOGO_SCALE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ColorField label="Primary" value={primaryColor} onChange={setPrimaryColor} />
            <ColorField label="Secondary" value={secondaryColor} onChange={setSecondaryColor} />
            <ColorField label="Accent" value={accentColor} onChange={setAccentColor} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Font family (CSS)</label>
              <input value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Google Fonts URL</label>
              <input value={fontUrl} onChange={(e) => setFontUrl(e.target.value)} className={inputClass} />
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="Commercial"
        subtitle="Pricing and policy defaults applied to every campaign on this channel. Advertisers see these as guardrails in the wizard."
        icon={DollarSign}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Coverage</label>
            <select value={coverage} onChange={(e) => setCoverage(e.target.value as 'regional' | 'national')} className={inputClass}>
              <option value="regional">Regional</option>
              <option value="national">National</option>
            </select>
            <p className="mt-1 text-[11px] text-slate-500">
              Regional channels show DMA targeting; national channels skip the geo step.
            </p>
          </div>
          <div>
            <label className={labelClass}>Rev share (%)</label>
            <input type="number" step="0.01" value={revSharePercent} onChange={(e) => setRevSharePercent(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              CTVBuyer's share of media spend. Your payout per buyer charge =
              campaign budget × (1 − rev share), released 60–90 days after the
              charge.
            </p>
          </div>
          <div>
            <label className={labelClass}>Min budget ($)</label>
            <input type="number" min={250} value={minBudget} onChange={(e) => setMinBudget(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              The smallest campaign the wizard will accept (pre-fee).
            </p>
          </div>
          <div>
            <label className={labelClass}>Min campaign days</label>
            <input type="number" value={minDays} onChange={(e) => setMinDays(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              Shortest flight duration available in the date picker.
            </p>
          </div>
          <div>
            <label className={labelClass}>Terms URL</label>
            <input value={termsUrl} onChange={(e) => setTermsUrl(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              Link shown next to the checkout "I agree" checkbox.
            </p>
          </div>
          <div className="sm:col-span-3">
            <label className={labelClass}>Support email <span className="font-normal normal-case tracking-normal text-slate-400">(optional)</span></label>
            <input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className={inputClass} />
            <p className="mt-1 text-[11px] text-slate-500">
              Surfaced in the wizard's footer so advertisers can reach the station directly.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Markets (DMAs)" subtitle="Where this channel reaches viewers." icon={MapPin}>
        <div className="space-y-4">
          {markets.map((m, i) => (
            <div key={i} className="space-y-2 border border-slate-200 rounded-xl p-3">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 sm:col-span-6">
                  <label className={labelClass}>DMA</label>
                  <select
                    value={m.dmaCode}
                    onChange={(e) => {
                      const dma = DMA_REGIONS.find((d) => d.code === e.target.value);
                      if (!dma) { updateMarket(i, { dmaCode: '', dmaName: '' }); return; }
                      if (markets.some((other, idx) => idx !== i && other.dmaCode === dma.code)) return;
                      // Auto-fill the state coverage from the DMA name when the
                      // staff editor hasn't already declared one (e.g., when
                      // switching DMAs on an existing row, keep their list).
                      const autoState = inferStateFromDmaName(dma.name);
                      const nextStates = m.coveredStates.length > 0
                        ? m.coveredStates
                        : (autoState ? [autoState] : []);
                      updateMarket(i, {
                        dmaCode: dma.code,
                        dmaName: dma.name,
                        coveredStates: nextStates,
                      });
                    }}
                    className={inputClass}
                  >
                    <option value="">— Select a DMA —</option>
                    {DMA_REGIONS.map((d) => (
                      <option key={d.code} value={d.code} disabled={markets.some((other, idx) => idx !== i && other.dmaCode === d.code)}>
                        {d.code} — {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-10 sm:col-span-5">
                  <label className={labelClass}>Channels (comma-separated)</label>
                  <input value={m.channels} onChange={(e) => updateMarket(i, { channels: e.target.value })} placeholder="WISC-TV" className={inputClass} />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setMarkets((arr) => arr.filter((_, idx) => idx !== i))}
                    className="p-2.5 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                    aria-label="Remove market"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>
                  Covered states (comma-separated, 2-letter codes)
                </label>
                <StateSelector
                  selectedStates={m.coveredStates}
                  onChange={(next) => updateMarket(i, { coveredStates: next })}
                  accentColor={accentColor}
                  showMap={false}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Surfaced in the wizard as "Suggested states" when an advertiser narrows their targeting. Add border states a station reaches (e.g., KEZI in Eugene also reaches Northern California, so <code>OR, CA</code>).
                </p>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setMarkets((arr) => [...arr, { dmaCode: '', dmaName: '', channels: '', coveredStates: [] }])}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 hover:text-slate-900 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add market
          </button>
        </div>
      </Section>

      <Section
        title="Run of Network"
        subtitle="Every campaign on this channel runs as Run-of-Network across the Publica sub-channels selected below. Adjust the CPM and uncheck any sub-channels you don't want to include."
        icon={Tv}
      >
        <div className="space-y-4">
          {subChannelsWarning && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <XCircle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
              <p className="text-[12px] text-amber-800 leading-relaxed">{subChannelsWarning}</p>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-4">
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 sm:col-span-4">
                <label className={labelClass}>Product</label>
                <input value="Run of Network" readOnly className={readonlyInputClass} />
              </div>
              <div className="col-span-12 sm:col-span-3">
                <label className={labelClass}>CPM ($)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={ronCpm}
                  onChange={(e) => setRonCpm(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="col-span-12 sm:col-span-5 flex sm:justify-end items-end">
                {availableSubChannels.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setIncludedSubChannels(
                        allChecked ? new Set() : new Set(availableSubChannels),
                      )
                    }
                    className="text-[12px] font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-2 decoration-1"
                  >
                    {allChecked ? 'Uncheck all' : 'Check all'}
                  </button>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelClass}>
                  Sub-channels
                  {availableSubChannels.length > 0 && (
                    <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">
                      ({includedSubChannels.size}/{availableSubChannels.length} included)
                    </span>
                  )}
                </label>
                {subChannelsLoading && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                )}
              </div>
              {availableSubChannels.length === 0 ? (
                <p className="text-[12px] text-slate-500 leading-relaxed px-1">
                  {organizationId && name.trim()
                    ? 'No matching Publica sub-channels found yet. Adjust the internal name to match a Publica channel prefix.'
                    : 'Pick an organization and enter the internal name above to load available Publica sub-channels.'}
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {availableSubChannels.map((channel) => (
                    <label
                      key={channel}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={includedSubChannels.has(channel)}
                        onChange={() => toggleSubChannel(channel)}
                        className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                      />
                      <span className="text-[13px] text-slate-700 truncate">{channel}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50 transition-colors"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitting ? 'Saving…' : (submitLabel ?? (mode === 'create' ? 'Create publisher' : 'Save changes'))}
        </button>
      </div>
    </form>
  );
};

interface SectionProps {
  title: string;
  subtitle?: string;
  icon: React.FC<{ className?: string }>;
  children: React.ReactNode;
}

function Section({ title, subtitle, icon: Icon, children }: SectionProps) {
  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden"
      style={{ boxShadow: '0 1px 0 rgba(15,23,42,0.02), 0 8px 24px -12px rgba(15,23,42,0.08)' }}
    >
      <header className="flex items-start gap-3 px-5 sm:px-6 py-4 border-b border-slate-200/70">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100 text-slate-700 shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">{title}</h3>
          {subtitle && <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>}
        </div>
      </header>
      <div className="px-5 sm:px-6 py-5">{children}</div>
    </section>
  );
}

const ColorField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white pl-1.5 pr-3 py-1.5 focus-within:border-slate-900 transition-colors">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 rounded-md border-0 cursor-pointer bg-transparent p-0"
        aria-label={`${label} color picker`}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="flex-1 min-w-0 bg-transparent border-0 px-1 py-0.5 text-[13px] font-mono tabular-nums text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-0"
      />
    </div>
  </div>
);

export default PublisherForm;
